import React, { useState, useEffect } from 'react';
import fetch from 'isomorphic-unfetch';
import { useRouter } from 'next/router';
import { buildDataUrl } from '@helper/api';
import PropTypes from 'prop-types';
import { appSetPageData } from '@redux/actions/app';
import { wrapper } from '@redux/store';
import ContentBlocks from '@components/organisms/content-blocks/contentBlocks';
import EducationTabletFinished from '@components/organisms/education/tablet-finished/educationTabletFinished';
import EducationTabletHeader from '@components/organisms/education/tablet-header/educationTabletHeader';
import EducationTabletProgress from '@components/organisms/education/tablet-progress/educationTabletProgress';
import EducationTabletTemplate from '@components/organisms/education/tablet-template/educationTabletTemplate';
import EducationTabletStage from '@components/organisms/education/tablet-stage/educationTabletStage';

export const getServerSideProps = wrapper.getServerSideProps(
  store =>
    async ({ params, locale, query }) => {
      const { slug } = params;
      const { token } = query;

      const localizedSlug = locale === 'de' ? 'bildung' : 'education';

      try {
        const response = await fetch(
          `${buildDataUrl(
            `${locale || 'de'}/${process.env.NEXT_PUBLIC_JSON_API_PATH}/${localizedSlug}/detail/${slug}${
              token ? `?token=${token}` : ''
            }`
          )}`,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            return {
              notFound: true
            };
          }
          throw new Error(`Failed to fetch data: ${response.status}`);
        }

        const data = await response.json();

        await store.dispatch(appSetPageData(data));

        return {
          props: {
            data
          }
        };
      } catch (error) {
        console.error('Error fetching data:', error);
        return {
          props: {
            data: null,
            error: error.message
          }
        };
      }
    }
);

function Steps({ data = null }) {
  const router = useRouter();

  const [activeIndex, setActiveIndex] = useState(1);
  const prevIndex = activeIndex - 1;
  const nextIndex = activeIndex + 1;

  const isFinished = activeIndex === data.workshopSteps.length + 1;

  const stepsWithLastStep = Array.from({ length: data.workshopSteps.length + 1 }, (_, index) => ({
    number: index + 1
  }));

  const uri = `${data.lang === 'de' ? '/de/bildung' : '/en/education'}/detail/${router.query.slug}`;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const index = parseInt(urlParams.get('step'), 10);
    if (!Number.isNaN(index) && index >= 1 && index <= stepsWithLastStep.length) {
      setActiveIndex(index);
    }
  }, [router.asPath]);

  const topic = data.educationTopics ? data.educationTopics[0].slug : '';

  let shapeBurst;

  if (topic === 'work') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Red.mp4';
  } else if (topic === 'energy') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Purple.mp4';
  } else if (topic === 'nutrition') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Green.mp4';
  } else if (topic === 'health') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Orange.mp4';
  } else if (topic === 'mobility') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Yellow.mp4';
  } else if (topic === 'city') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Blue.mp4';
  } else if (topic === 'democracy') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Magenta.mp4';
  } else if (topic === 'ressources') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Light-Purple.mp4';
  } else if (topic === 'agriculture') {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Light-Green.mp4';
  } else {
    shapeBurst = '/static/education/videos/ProgressBar-ShapeBurst-Black.mp4';
  }

  return (
    <EducationTabletTemplate>
      <EducationTabletHeader uri={uri} />
      {data.workshopSteps.map((item, index) => {
        if (index + 1 === activeIndex) {
          return (
            <div key={item.id}>
              <EducationTabletStage
                number={index + 1}
                headline={item.headline}
                introCopy={item.introCopy}
                educationTopics={data.educationTopics}
                educationType={data.educationType}
              />
              <ContentBlocks noShareLinks embeds={item.embeds} />
            </div>
          );
        }
        return null;
      })}
      {isFinished && <EducationTabletFinished shapeBurst={shapeBurst} />}
      <EducationTabletProgress
        stepsWithLastStep={stepsWithLastStep}
        prevIndex={prevIndex}
        nextIndex={nextIndex}
        educationTopics={data.educationTopics}
        activeIndex={activeIndex}
        shapeBurst={shapeBurst}
        topic={topic}
        uri={uri}
      />
    </EducationTabletTemplate>
  );
}

Steps.propTypes = {
  data: PropTypes.shape({
    lang: PropTypes.string,
    workshopSteps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        headline: PropTypes.string,
        introCopy: PropTypes.string,
        embeds: PropTypes.arrayOf(PropTypes.shape())
      })
    ),
    educationTopics: PropTypes.arrayOf(PropTypes.shape()),
    educationType: PropTypes.arrayOf(PropTypes.shape())
  })
};

export default Steps;
